import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Bell, Users, AlertTriangle, ChevronRight, Flame, Ticket } from 'lucide-react';
import api from '../utils/api';

// Time display component
const TimeUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="bg-black text-white px-3 py-2 md:px-4 md:py-3 font-mono text-xl md:text-3xl font-black border-2 border-white shadow-[4px_4px_0px_rgba(255,255,255,0.3)]">
      {String(value).padStart(2, '0')}
    </div>
    <span className="text-xs md:text-sm font-bold uppercase mt-1 tracking-wider">{label}</span>
  </div>
);

const DropCountdown = ({ drop, onJoinNotify, onEnterRaffle, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [raffleLoading, setRaffleLoading] = useState(false);
  const [userNotified, setUserNotified] = useState(drop.userNotified || false);
  const [userRaffleEntry, setUserRaffleEntry] = useState(drop.userRaffleEntry || null);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [showRaffleForm, setShowRaffleForm] = useState(false);

  // Calculate time remaining
  const calculateTimeLeft = useCallback(() => {
    const now = new Date().getTime();
    const dropTime = new Date(drop.dropDate).getTime();
    const endTime = drop.endDate ? new Date(drop.endDate).getTime() : null;
    
    // Check if ended
    if (endTime && now > endTime) {
      setHasEnded(true);
      setIsLive(false);
      return null;
    }
    
    // Check if live
    if (now >= dropTime && (!endTime || now < endTime)) {
      setIsLive(true);
      if (endTime) {
        const diff = endTime - now;
        return {
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        };
      }
      return null;
    }
    
    // Countdown to drop
    const diff = dropTime - now;
    if (diff <= 0) {
      setIsLive(true);
      return null;
    }
    
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  }, [drop.dropDate, drop.endDate]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const handleJoinNotify = async () => {
    if (!onJoinNotify) return;
    setNotifyLoading(true);
    try {
      await onJoinNotify(drop._id);
      setUserNotified(true);
    } catch (err) {
      console.error('Failed to join notify list:', err);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleEnterRaffle = async () => {
    if (!onEnterRaffle) return;
    
    const selections = Object.entries(selectedSizes).map(([productId, size]) => ({
      product: productId,
      size
    }));
    
    if (selections.length === 0) {
      alert('Please select at least one size');
      return;
    }
    
    setRaffleLoading(true);
    try {
      const result = await onEnterRaffle(drop._id, selections);
      setUserRaffleEntry(result.data);
      setShowRaffleForm(false);
    } catch (err) {
      console.error('Failed to enter raffle:', err);
    } finally {
      setRaffleLoading(false);
    }
  };

  const handleSizeSelect = (productId, size) => {
    setSelectedSizes(prev => ({
      ...prev,
      [productId]: prev[productId] === size ? null : size
    }));
  };

  // Compact version (for cards/lists)
  if (compact) {
    return (
      <div className="bg-black text-white p-4 border-4 border-yellow-400">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Flame size={20} className="text-yellow-400" />
            <span className="font-black uppercase text-sm tracking-wider">
              {isLive ? 'Live Now' : 'Coming Soon'}
            </span>
          </div>
          {drop.isLimited && (
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 uppercase">
              Limited
            </span>
          )}
        </div>
        
        {timeLeft && (
          <div className="flex gap-2 justify-center my-3">
            <TimeUnit value={timeLeft.days} label="Days" />
            <span className="text-2xl font-black self-start pt-2">:</span>
            <TimeUnit value={timeLeft.hours} label="Hrs" />
            <span className="text-2xl font-black self-start pt-2">:</span>
            <TimeUnit value={timeLeft.minutes} label="Min" />
            <span className="text-2xl font-black self-start pt-2">:</span>
            <TimeUnit value={timeLeft.seconds} label="Sec" />
          </div>
        )}
        
        {isLive && (
          <button className="w-full bg-yellow-400 text-black font-black py-3 uppercase tracking-wider border-2 border-white hover:bg-white transition-colors">
            Shop Drop
          </button>
        )}
        
        {!isLive && !userNotified && (
          <button 
            onClick={handleJoinNotify}
            disabled={notifyLoading}
            className="w-full bg-white text-black font-black py-3 uppercase tracking-wider border-2 border-yellow-400 hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2"
          >
            <Bell size={18} />
            {notifyLoading ? '...' : 'Notify Me'}
          </button>
        )}
        
        {!isLive && userNotified && (
          <div className="w-full bg-green-600 text-white font-black py-3 uppercase tracking-wider text-center flex items-center justify-center gap-2">
            <Bell size={18} />
            You're on the list
          </div>
        )}
      </div>
    );
  }

  // Full version
  return (
    <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black]">
      {/* Hero Image */}
      {drop.heroImage && (
        <div className="relative aspect-video bg-gray-100 overflow-hidden">
          <img 
            src={drop.heroImage} 
            alt={drop.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          
          {/* Status Badge */}
          <div className="absolute top-4 left-4">
            {isLive ? (
              <div className="bg-red-600 text-white px-4 py-2 font-black uppercase tracking-wider flex items-center gap-2 border-2 border-white shadow-[4px_4px_0px_rgba(0,0,0,0.3)]">
                <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
                Live Now
              </div>
            ) : hasEnded ? (
              <div className="bg-gray-600 text-white px-4 py-2 font-black uppercase tracking-wider border-2 border-white">
                Ended
              </div>
            ) : (
              <div className="bg-yellow-400 text-black px-4 py-2 font-black uppercase tracking-wider border-2 border-black">
                Coming Soon
              </div>
            )}
          </div>
          
          {/* Collaboration Badge */}
          {drop.isCollab && (
            <div className="absolute top-4 right-4 bg-white text-black px-4 py-2 font-black uppercase tracking-wider border-2 border-black">
              Collab: {drop.collaboratorName}
            </div>
          )}
        </div>
      )}
      
      {/* Content */}
      <div className="p-6">
        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-2">
          {drop.name}
        </h2>
        
        {drop.description && (
          <p className="text-gray-600 mb-6 font-mono text-sm md:text-base">
            {drop.description}
          </p>
        )}
        
        {/* Countdown Timer */}
        {timeLeft && (
          <div className="bg-black text-white p-6 border-4 border-yellow-400 mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock size={24} className="text-yellow-400" />
              <span className="font-black uppercase tracking-wider">
                {isLive ? 'Ending In' : 'Drops In'}
              </span>
            </div>
            
            <div className="flex gap-3 md:gap-4 justify-center">
              <TimeUnit value={timeLeft.days} label="Days" />
              <span className="text-3xl md:text-4xl font-black self-start pt-2">:</span>
              <TimeUnit value={timeLeft.hours} label="Hours" />
              <span className="text-3xl md:text-4xl font-black self-start pt-2">:</span>
              <TimeUnit value={timeLeft.minutes} label="Mins" />
              <span className="text-3xl md:text-4xl font-black self-start pt-2">:</span>
              <TimeUnit value={timeLeft.seconds} label="Secs" />
            </div>
          </div>
        )}
        
        {/* Live Actions */}
        {isLive && !drop.isRaffle && (
          <div className="space-y-4">
            <button className="w-full bg-yellow-400 text-black py-4 font-black text-xl uppercase tracking-wider border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center justify-center gap-3">
              <Flame size={24} />
              Shop The Drop
              <ChevronRight size={24} />
            </button>
            
            {drop.useQueue && (
              <div className="bg-orange-100 border-2 border-orange-400 p-4 flex items-center gap-3">
                <Users size={24} className="text-orange-600" />
                <div>
                  <p className="font-bold text-orange-800">High Demand Expected</p>
                  <p className="text-sm text-orange-600">Queue system will activate if traffic is high</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Raffle Section */}
        {drop.isRaffle && !isLive && !hasEnded && (
          <div className="bg-purple-50 border-4 border-purple-400 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Ticket size={32} className="text-purple-600" />
              <div>
                <h3 className="font-black text-xl uppercase text-purple-900">Raffle Entry</h3>
                <p className="text-sm text-purple-600">Enter for a chance to purchase</p>
              </div>
            </div>
            
            {userRaffleEntry ? (
              <div className="bg-white border-2 border-purple-400 p-4 text-center">
                <p className="font-bold text-purple-900 mb-2">
                  Entry Status: {userRaffleEntry.status.toUpperCase()}
                </p>
                <p className="text-sm text-gray-600">
                  Draw Date: {new Date(drop.raffleDrawDate).toLocaleDateString()}
                </p>
              </div>
            ) : showRaffleForm ? (
              <div className="space-y-4">
                <p className="text-sm font-bold text-purple-800">
                  Select your preferred sizes:
                </p>
                
                {drop.products.map((product) => (
                  <div key={product.product._id} className="bg-white border-2 border-purple-200 p-3">
                    <p className="font-bold text-sm mb-2">{product.product.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size) => (
                        <button
                          key={size.size}
                          onClick={() => handleSizeSelect(product.product._id, size.size)}
                          className={`px-3 py-1 text-sm font-bold border-2 transition-all ${
                            selectedSizes[product.product._id] === size.size
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-purple-600 border-purple-200 hover:border-purple-400'
                          }`}
                          disabled={size.stock <= 0}
                        >
                          {size.size}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRaffleForm(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 font-bold uppercase"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEnterRaffle}
                    disabled={raffleLoading || Object.keys(selectedSizes).length === 0}
                    className="flex-1 bg-purple-600 text-white py-3 font-black uppercase tracking-wider disabled:opacity-50"
                  >
                    {raffleLoading ? 'Entering...' : 'Enter Raffle'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowRaffleForm(true)}
                className="w-full bg-purple-600 text-white py-4 font-black uppercase tracking-wider border-2 border-purple-800 hover:bg-purple-700 transition-colors"
              >
                Enter Raffle
              </button>
            )}
            
            {drop.raffleDrawDate && (
              <p className="text-center text-sm text-purple-600 mt-3">
                Winners drawn on {new Date(drop.raffleDrawDate).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
        
        {/* Notify Section */}
        {!isLive && !hasEnded && !drop.isRaffle && (
          <div className="space-y-4">
            {userNotified ? (
              <div className="bg-green-100 border-4 border-green-400 p-4 flex items-center gap-3">
                <Bell size={24} className="text-green-600" />
                <div>
                  <p className="font-bold text-green-800">You're on the notify list!</p>
                  <p className="text-sm text-green-600">We'll email you when this drops.</p>
                </div>
              </div>
            ) : (
              <button
                onClick={handleJoinNotify}
                disabled={notifyLoading}
                className="w-full bg-black text-white py-4 font-black text-xl uppercase tracking-wider border-4 border-yellow-400 shadow-[4px_4px_0px_rgba(255,255,255,0.3)] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center justify-center gap-3"
              >
                <Bell size={24} />
                {notifyLoading ? 'Joining...' : 'Notify Me When Live'}
              </button>
            )}
            
            {/* Stats */}
            <div className="flex gap-4 text-sm">
              {drop.notifyUsers?.length > 0 && (
                <div className="flex items-center gap-1 text-gray-500">
                  <Users size={16} />
                  <span>{drop.notifyUsers.length} waiting</span>
                </div>
              )}
              {drop.isLimited && (
                <div className="flex items-center gap-1 text-orange-600">
                  <AlertTriangle size={16} />
                  <span>Limited quantity</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Ended Message */}
        {hasEnded && (
          <div className="bg-gray-100 border-4 border-gray-400 p-6 text-center">
            <p className="font-black text-2xl uppercase text-gray-600 mb-2">This Drop Has Ended</p>
            <p className="text-gray-500">Check out our latest drops</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DropCountdown;
